import * as React from 'react';

const ZOOM_LEVEL = 2;
const PAN_SPEED_MULTIPLIER = 2;

interface UseImageZoomReturn {
  isZoomed: boolean;
  zoomContainerRef: React.RefObject<HTMLDivElement>;
  zoomLevel: number;
  handleZoomClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  resetZoom: () => void;
}

export const useImageZoom = (): UseImageZoomReturn => {
  const [isZoomed, setIsZoomed] = React.useState(false);
  const zoomContainerRef = React.useRef<HTMLDivElement>(null);

  const updateZoomOrigin = React.useCallback(
    (clientX: number, clientY: number) => {
      if (!zoomContainerRef.current) return;
      const rect = zoomContainerRef.current.getBoundingClientRect();
      // Normalize mouse position to 0-1, then amplify distance from center (0.5)
      // so the user doesn't need to reach the very edges to pan fully.
      const rawX = (clientX - rect.left) / rect.width;
      const rawY = (clientY - rect.top) / rect.height;
      const x = Math.min(
        100,
        Math.max(0, (0.5 + (rawX - 0.5) * PAN_SPEED_MULTIPLIER) * 100)
      );
      const y = Math.min(
        100,
        Math.max(0, (0.5 + (rawY - 0.5) * PAN_SPEED_MULTIPLIER) * 100)
      );
      zoomContainerRef.current.style.setProperty(
        '--zoom-origin',
        `${x}% ${y}%`
      );
    },
    []
  );

  const handleZoomClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setIsZoomed((prev) => {
        const next = !prev;
        if (next) {
          updateZoomOrigin(e.clientX, e.clientY);
        }
        return next;
      });
    },
    [updateZoomOrigin]
  );

  const resetZoom = React.useCallback(() => {
    setIsZoomed(false);
  }, []);

  // Track mouse globally while zoomed so the image pans even outside the container
  React.useEffect(() => {
    if (!isZoomed) return;

    const handleMouseMove = (e: MouseEvent) => {
      updateZoomOrigin(e.clientX, e.clientY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isZoomed, updateZoomOrigin]);

  return {
    isZoomed,
    zoomContainerRef,
    zoomLevel: ZOOM_LEVEL,
    handleZoomClick,
    resetZoom,
  };
};
